import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue ${user.prenom} !`);
      if (user.role === 'livreur') navigate('/livreur');
      else navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Image */}
      <div
        className="hidden lg:flex flex-1 items-center justify-center relative"
        style={{
          background: 'linear-gradient(135deg, hsl(222,47%,11%) 0%, hsl(222,47%,25%) 100%)',
        }}
      >
        <div className="text-white text-center p-12">
          <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl font-bold text-white">IBN</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">CRM IBN Services</h1>
          <p className="text-blue-200 text-lg">Gestion des dossiers clients<br />Kloss & Push Club</p>
          <div className="mt-12 grid grid-cols-3 gap-4 text-center">
            {[['Clients', 'Gérés'], ['Documents', 'Automatiques'], ['Subventions', 'CARSAT']].map(([title, sub]) => (
              <div key={title} className="bg-white/10 rounded-xl p-4">
                <p className="font-semibold text-white">{title}</p>
                <p className="text-xs text-blue-200 mt-1">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 lg:max-w-md flex items-center justify-center p-8 bg-[hsl(210,20%,98%)]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-[hsl(222,47%,11%)] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-white">IBN</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[hsl(222,47%,11%)] mb-2">Connexion</h2>
          <p className="text-gray-500 mb-8">Accédez à votre espace CRM</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] focus:border-transparent bg-white"
                  placeholder="votre@email.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full pl-9 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] focus:border-transparent bg-white"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[hsl(222,47%,11%)] text-white py-2.5 rounded-lg font-medium text-sm hover:bg-[hsl(222,47%,18%)] transition-colors disabled:opacity-60 active:scale-95"
              data-testid="login-submit-btn"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
